import { supabase } from "@/integrations/supabase/client";
/**
 * Calculate the distance between two locations using the Haversine formula
 * @param lat1 Latitude of first location
 * @param lon1 Longitude of first location
 * @param lat2 Latitude of second location
 * @param lon2 Longitude of second location
 * @returns Distance in miles
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Get coordinates for a location using Mapbox Geocoding API
 * @param location The location string (city, postcode, address)
 * @returns Promise with coordinates { latitude, longitude } or null if not found
 */
export async function geocodeLocation(location: string): Promise<{ latitude: number; longitude: number } | null> {
  console.log('🗺️ Geocoding location:', location);
  
  try {
    // Get Mapbox token from the edge function via Supabase client (recommended)
    console.log('🔑 Fetching Mapbox token...');
    const { data, error } = await supabase.functions.invoke('get-mapbox-token', {
      method: 'GET',
    });

    if (error) {
      console.warn('❌ Failed to retrieve Mapbox token via edge function:', error.message);
      return null;
    }

    const token = (data as any)?.token;
    if (!token) {
      console.warn('❌ Mapbox token not available for geocoding');
      return null;
    }
    console.log('✅ Mapbox token retrieved successfully');

    const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json?access_token=${token}&country=GB&limit=1`;
    console.log('🌐 Making geocoding request for:', location);
    
    const geocodeResponse = await fetch(geocodeUrl);
    
    if (!geocodeResponse.ok) {
      console.error('❌ Geocoding request failed with status:', geocodeResponse.status);
      throw new Error('Geocoding request failed');
    }
    
    const payload = await geocodeResponse.json();
    console.log('📍 Geocoding response for', location, ':', payload);
    
    if (payload.features && payload.features.length > 0) {
      const [longitude, latitude] = payload.features[0].center;
      const result = { latitude, longitude };
      console.log('✅ Coordinates found for', location, ':', result);
      return result;
    }
    
    console.warn('⚠️ No coordinates found for location:', location);
    return null;
  } catch (error) {
    console.error('💥 Error geocoding location:', location, error);
    return null;
  }
}

/**
 * Cache for geocoded locations to avoid repeated API calls
 */
const geocodeCache = new Map<string, { latitude: number; longitude: number } | null>();
const cityPostcodeCache = new Map<string, { latitude: number; longitude: number } | null>();

/**
 * Get coordinates with caching
 * @param location The location string
 * @returns Promise with coordinates or null
 */
export async function getCachedCoordinates(location: string): Promise<{ latitude: number; longitude: number } | null> {
  const cacheKey = location.toLowerCase().trim();
  
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey) || null;
  }
  
  const coordinates = await geocodeLocation(location);
  geocodeCache.set(cacheKey, coordinates);
  
  return coordinates;
}

/**
 * Geocode favoring city/locality/region/postcode results to avoid street name collisions
 */
export async function getCityPostcodeCoordinates(location: string): Promise<{ latitude: number; longitude: number } | null> {
  const cacheKey = `city:${location.toLowerCase().trim()}`;
  if (cityPostcodeCache.has(cacheKey)) return cityPostcodeCache.get(cacheKey) || null;

  try {
    const { data, error } = await supabase.functions.invoke('get-mapbox-token', { method: 'GET' });
    if (error) return null;
    const token = (data as any)?.token;
    if (!token) return null;

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json?access_token=${token}&country=GB&types=place,locality,region,postcode&limit=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const payload = await res.json();
    if (payload.features && payload.features.length > 0) {
      const [longitude, latitude] = payload.features[0].center;
      const result = { latitude, longitude };
      cityPostcodeCache.set(cacheKey, result);
      return result;
    }
    cityPostcodeCache.set(cacheKey, null);
    return null;
  } catch {
    cityPostcodeCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Filter jobs by distance using only precise address coordinates
 * @param jobs Array of jobs to filter
 * @param tradesmanLocation Tradesman's business address
 * @param maxDistanceMiles Maximum distance in miles (default: 20)
 * @returns Promise with filtered jobs
 */
export async function filterJobsByPreciseAddress(
  jobs: any[], 
  tradesmanLocation: string, 
  maxDistanceMiles: number = 20
): Promise<any[]> {
  console.log('🎯 Starting PRECISE address filtering:', { 
    jobCount: jobs.length, 
    tradesmanLocation, 
    maxDistanceMiles 
  });

  if (!tradesmanLocation || jobs.length === 0) {
    console.log('⚠️ No tradesman location or no jobs - returning empty array');
    return [];
  }

  try {
    // Use only precise geocoding for business addresses - no fallbacks
    console.log('🏢 Geocoding tradesman business address (precise only):', tradesmanLocation);
    const tradesmanCoords = await getCachedCoordinates(tradesmanLocation);

    if (!tradesmanCoords) {
      console.warn('❌ Could not geocode tradesman business address - no jobs will be shown');
      return [];
    }

    console.log('✅ Tradesman coordinates:', tradesmanCoords);

    const filtered: any[] = [];

    for (const job of jobs) {
      const jobLocation = job.address || job.location || '';
      if (!jobLocation.trim()) {
        console.warn(`⚠️ Job ${job.job_number || job.id} has no address - skipping`);
        continue;
      }

      console.log(`🗺️ Geocoding job address (precise only): ${jobLocation} (Job #${job.job_number || job.id})`);
      
      // Use only precise geocoding for job addresses - no fallbacks
      const jobCoords = await getCachedCoordinates(jobLocation);

      if (!jobCoords) {
        console.warn(`❌ Could not geocode job address: ${jobLocation} - job excluded`);
        continue;
      }

      const distance = calculateDistance(
        tradesmanCoords.latitude,
        tradesmanCoords.longitude,
        jobCoords.latitude,
        jobCoords.longitude
      );
      
      console.log(`📏 Distance to job: ${Math.round(distance * 10) / 10} miles`);

      if (distance <= maxDistanceMiles) {
        filtered.push({ ...job, distance: Math.round(distance * 10) / 10 });
        console.log(`✅ Job ${job.job_number || job.id} included - within ${maxDistanceMiles} mile radius`);
      } else {
        console.log(`❌ Job ${job.job_number || job.id} excluded - ${Math.round(distance * 10) / 10} miles exceeds ${maxDistanceMiles} mile limit`);
      }
    }

    console.log(`📊 Precise address filtering complete: ${jobs.length} → ${filtered.length} within ${maxDistanceMiles} miles`);
    return filtered;
  } catch (error) {
    console.error('💥 Error in precise address filtering:', error);
    return [];
  }
}

/**
 * Filter jobs by distance from tradesman location (DEPRECATED - use filterJobsByPreciseAddress)
 * @param jobs Array of jobs to filter
 * @param tradesmanLocation Tradesman's registered location
 * @param maxDistanceMiles Maximum distance in miles (default: 20)
 * @returns Promise with filtered jobs
 */
export async function filterJobsByDistance(
  jobs: any[], 
  tradesmanLocation: string, 
  maxDistanceMiles: number = 20
): Promise<any[]> {
  console.log('⚠️ DEPRECATED: filterJobsByDistance called - redirecting to precise address filtering');
  return filterJobsByPreciseAddress(jobs, tradesmanLocation, maxDistanceMiles);
}